import { alpha } from '@mui/material/styles'
import { r4r } from 'styles/theme'
import {
  RESERVATION_STATUS,
  ReservationStatus
} from 'services/commonRooms/commonRooms.consts'

// Visual helpers for the "Panel Klienta 3.0" Helpdesk section (Figma).

export interface BadgeColors {
  backgroundColor: string
  color: string
}

// Fault ticket status → badge colors.
// Desktop uses tinted pills, mobile uses solid badges (as in the Figma designs).
export const getFaultStatusColors = (code: string, isDesktop: boolean): BadgeColors => {
  switch (code) {
    case 'DURING':
      return isDesktop
        ? { backgroundColor: alpha(r4r.yellow, 0.15), color: '#B8920A' }
        : { backgroundColor: r4r.yellow, color: r4r.ink }
    case 'READY':
      return isDesktop
        ? { backgroundColor: alpha(r4r.cyan, 0.2), color: '#0E7C86' }
        : { backgroundColor: alpha(r4r.cyan, 0.2), color: r4r.cyan }
    default:
      return isDesktop
        ? { backgroundColor: alpha(r4r.navy, 0.1), color: r4r.navy }
        : { backgroundColor: r4r.navy, color: '#fff' }
  }
}

// Ordered fault timeline steps driven by the status code.
export const FAULT_TIMELINE_STEPS = [
  { code: 'NEW', i18n: { id: 'helpdesk.timeline.new', defaultMessage: 'Nowe' } },
  { code: 'DURING', i18n: { id: 'helpdesk.timeline.during', defaultMessage: 'W toku' } },
  { code: 'READY', i18n: { id: 'helpdesk.timeline.ready', defaultMessage: 'Gotowe' } }
]

export const getFaultTimelineActiveIndex = (code: string) => {
  switch (code) {
    case 'DURING':
      return 1
    case 'READY':
      return 2
    default:
      return 0
  }
}

// Gradient tiles used for fault category icons (mobile cards / detail hero).
const FAULT_GRADIENTS = [
  r4r.gradient,
  r4r.gradientViolet,
  r4r.gradientCyan,
  'linear-gradient(135deg, #6A8AE5 0%, #24D4E1 100%)'
]

export const getFaultGradient = (seed: number) =>
  FAULT_GRADIENTS[Math.abs(seed) % FAULT_GRADIENTS.length]

// Tinted icon chip colors used on the desktop ticket list.
const FAULT_TINTS = [r4r.azure, r4r.violet, r4r.cyan]

export const getFaultTint = (seed: number) => FAULT_TINTS[Math.abs(seed) % FAULT_TINTS.length]

// Reservation status → badge colors.
export const getReservationStatusColors = (
  status: ReservationStatus,
  isDesktop: boolean
): BadgeColors => {
  switch (status) {
    case RESERVATION_STATUS.PENDING:
      return isDesktop
        ? { backgroundColor: alpha(r4r.yellow, 0.15), color: '#D4A800' }
        : { backgroundColor: r4r.yellow, color: r4r.ink }
    case RESERVATION_STATUS.CONFIRMED:
      return isDesktop
        ? { backgroundColor: alpha(r4r.green, 0.15), color: r4r.green }
        : { backgroundColor: r4r.green, color: '#fff' }
    case RESERVATION_STATUS.CANCELLED:
      return isDesktop
        ? { backgroundColor: alpha(r4r.red, 0.1), color: r4r.red }
        : { backgroundColor: r4r.red, color: '#fff' }
    default:
      // completed / no_show
      return isDesktop
        ? { backgroundColor: alpha(r4r.grey, 0.15), color: r4r.grey }
        : { backgroundColor: r4r.grey, color: '#fff' }
  }
}

// Gradient tiles for reservation list rows / detail hero (per Figma status coloring).
export const getReservationGradient = (status: ReservationStatus) => {
  switch (status) {
    case RESERVATION_STATUS.PENDING:
      return r4r.gradientViolet
    case RESERVATION_STATUS.CONFIRMED:
      return r4r.gradient
    case RESERVATION_STATUS.CANCELLED:
      return 'linear-gradient(135deg, #6B7280 0%, #9CA3AF 100%)'
    default:
      // completed / no_show
      return r4r.gradientCyan
  }
}
